import React, { memo } from 'react';
import { GRID_HALF_SIZE, GridState, apply, childOffset, compose, inverse, orientationElement } from '../geometry';
import { rotateGrid } from '../ultrarubigramUtils';
import Quadrant from './Quadrant';
import RotateButton from './RotateButton';

type GridProps = {
  g: number;
  grid: GridState;
  /** Screen orientation of the whole puzzle. */
  parent: number;
  interactive: boolean;
  /** Render one of the copies of the piece tree instead: the border bands, or the tabs on 12x12 seams (see Board). */
  layer?: 'bands' | 'seamTabs';
};

const Grid = memo(({ g, grid, parent, interactive, layer }: GridProps) => {
  const self = compose(parent, orientationElement(grid));
  const [centerX, centerY] = apply(parent, ...childOffset(g, GRID_HALF_SIZE));

  const flipperStyle: React.CSSProperties = {
    transform: `rotateY(${grid.flipH ? 180 : 0}deg)`
      + ` rotateX(${grid.flipV ? 180 : 0}deg)`
      + ` rotateZ(${grid.rot}deg)`,
  };

  const contentsClasses = 'ultraContents'
    + (grid.isFlipping ? ' isFlipping' : '')
    + (grid.moving ? ' isMoving' : '');

  const moving = grid.moving || grid.quadrants.some(quadrant =>
    quadrant.moving || quadrant.subgrids.some(subgrid => subgrid.moving));
  const pieceClasses = `ultraPiece ultraGrid pos${g}${moving ? ' moving' : ''}`
    + (layer === 'bands' ? ' bands' : layer ? ' tabs' : '');

  const quadrants = (quadrantLayer?: 'bands' | 'innerTabs' | 'seamTabs') => grid.quadrants.map((quadrant, q) => (
    <Quadrant
      key={`${quadrantLayer ?? 'piece'}${q}`}
      g={g}
      q={q}
      quadrant={quadrant}
      parent={self}
      parentCenterX={centerX}
      parentCenterY={centerY}
      interactive={interactive}
      layer={quadrantLayer}
      ancestorMoving={grid.moving}
    />
  ));

  return (
    <div className={pieceClasses}>
      <div className="ultraFlipper" style={flipperStyle}>
        <div className={contentsClasses}>
          {quadrants(layer)}

          {/*
            The 6x6 flip tabs on this grid's 6x6 seams and outer edges, in copies of the
            quadrants that sit above all of them. A turning 3x3 passes under its 6x6's tabs;
            a turning 6x6 passes over its neighbours' (see .bigMoving).
          */}
          {interactive && !layer && quadrants('innerTabs')}

          {interactive && !layer &&
            <RotateButton level="grid" upright={inverse(self)} onRotate={cw => rotateGrid(g, cw)}/>}
        </div>
      </div>
    </div>
  );
});

export default Grid;
